import { v4 as uuidv4 } from "uuid";
import type {
    PlainWorkflow,
    PlainWorkflowAction,
    PlainWorkflowStep,
    PlainWorkflowTrigger,
} from "@/queries/automation-queries";
import { type PlainMessage } from "@bufbuild/protobuf";
import {
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTrigger_PullRequest,
    WorkflowTrigger_Time,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
    WorkflowTriggerContext_Repositories,
    type WorkflowStep,
    type WorkflowTrigger_PullRequestEvent,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";


export type WorkflowEditFormLimitsData = {
    maxParallel?: number;
    maxTotal?: number;
};

export type WorkflowEditFormData = {
    executor?: PlainMessage<Subject>;
    triggers: WorkflowEditFormTriggerData[];
    steps: WorkflowEditFormStepData[];
    limits?: WorkflowEditFormLimitsData;
};

export type WorkflowEditFormTriggerType = "manual" | "time" | "pullRequest";

/**
 * The type of contexts for a manually-triggered workflow.
 */
export enum WorkflowEditFormTriggerContextType {
    /**
     * Targets external SCM repositories the workflow should work with.
     */
    RepositoriesSearch = "repositories",

    RepositoriesList = "repositories-list",

    /**
     * Targets projects the workflow should work with.
     */
    Projects = "projects",
}

/**
 * A manual trigger context that specifies the projects the workflow should work with
 */
export type WorkflowEditFormTriggerProjectContext = {
    type: WorkflowEditFormTriggerContextType.Projects;
    projectIds: string[];
};

/**
 * A manual trigger context that specifies repositories the workflow should work with
 * using a search expression
 */
export type WorkflowEditFormTriggerRepositoryContext = {
    type: WorkflowEditFormTriggerContextType.RepositoriesSearch;

    /**
     * An scm-specific search string that will be used to find repositories to work with
     */
    repoSearchString: string;

    /**
     * The SCM host where the search should be performed (e.g., "github.com", "gitlab.com")
     */
    scmHost: string;

    /**
     * The ID of the runner that will be used for the workflow.
     */
    runnerId: string;

    /**
     * The ID of the environment class that will be used for the workflow.
     */
    environmentClassId: string;
};

/**
 * A manual trigger context that specifies repositories the workflow should work with
 * using a list of repository URLs.
 */
export type WorkflowEditFormTriggerRepositoryListContext = {
    type: WorkflowEditFormTriggerContextType.RepositoriesList;

    /**
     * The ID of the environment class that will be used for the workflow.
     */
    environmentClassId: string;

    /**
     * List of explicit repository URLs (list mode).
     */
    repoUrls: string[];
};

export type WorkflowEditFormTriggerContext =
    | WorkflowEditFormTriggerProjectContext
    | WorkflowEditFormTriggerRepositoryContext
    | WorkflowEditFormTriggerRepositoryListContext;

export type WorkflowEditFormTriggerConfigurationData = {
    time?: { cronExpression: string };
    pullRequest?: { events: WorkflowTrigger_PullRequestEvent[]; webhookId?: string };
};

export type WorkflowEditFormTriggerData = {
    id: string; // temporary ID for form management
    type: WorkflowEditFormTriggerType;
    context: WorkflowEditFormTriggerContext;
    config: WorkflowEditFormTriggerConfigurationData;
};

export type WorkflowEditFormStepType = "command" | "prompt" | "pr" | "report";

export type WorkflowEditFormPullRequestStepData = {
    type: "pr";
    id: string; // temporary ID for form management
    title: string;
    description: string;
    branch: string;
    isDraft: boolean;
};

export type WorkflowEditFormPromptStepData = {
    type: "prompt";
    id: string; // temporary ID for form management
    content: string;
};

export type WorkflowEditFormCommandStepData = {
    type: "command";
    id: string; // temporary ID for form management
    content: string;
};

export type ReportOutputSchemaType = "string" | "integer" | "float" | "boolean";

export type ReportOutputExtractionType = "prompt" | "command";

export type WorkflowEditFormReportOutputData = {
    id: string; // temporary ID for form management
    title: string;
    key: string;
    schemaType: ReportOutputSchemaType;
    // Schema constraints (optional)
    stringPattern?: string;
    integerMin?: number;
    integerMax?: number;
    floatMin?: number;
    floatMax?: number;
    // Acceptance criteria (optional CEL expression)
    acceptanceCriteria?: string;
    // Extraction method
    extractionType: ReportOutputExtractionType;
    extractionValue: string;
};

export type WorkflowEditFormReportStepData = {
    type: "report";
    id: string; // temporary ID for form management
    outputs: WorkflowEditFormReportOutputData[];
};

export type WorkflowEditFormStepData =
    | WorkflowEditFormPullRequestStepData
    | WorkflowEditFormPromptStepData
    | WorkflowEditFormCommandStepData
    | WorkflowEditFormReportStepData;

export const convertWorkflowActionToFormSteps = (action?: PlainWorkflowAction): WorkflowEditFormStepData[] => {
    if (!action?.steps || action.steps.length === 0) {
        return [];
    }

    return action.steps.map((step, index) => {
        const id = `step-${index}-${uuidv4()}`;
        if (step.step?.case === "task") {
            return {
                id,
                type: "command" as const,
                content: step.step.value.command || "",
            };
        } else if (step.step?.case === "agent") {
            return {
                id,
                type: "prompt" as const,
                content: step.step.value.prompt || "",
            };
        } else if (step.step?.case === "pullRequest") {
            return {
                id,
                type: "pr" as const,
                title: step.step.value.title || "",
                description: step.step.value.description || "",
                branch: step.step.value.branch || "",
                isDraft: step.step.value.draft || false,
            };
        } else if (step.step?.case === "report") {
            return {
                id,
                type: "report" as const,
                outputs: (step.step.value.outputs || []).map((output, outputIndex) => {
                    const outputId = `output-${outputIndex}-${uuidv4()}`;
                    let schemaType: ReportOutputSchemaType = "string";
                    let stringPattern: string | undefined;
                    let integerMin: number | undefined;
                    let integerMax: number | undefined;
                    let floatMin: number | undefined;
                    let floatMax: number | undefined;

                    if (output.schema?.case === "string") {
                        schemaType = "string";
                        stringPattern = output.schema.value.pattern || undefined;
                    } else if (output.schema?.case === "integer") {
                        schemaType = "integer";
                        integerMin = output.schema.value.min ?? undefined;
                        integerMax = output.schema.value.max ?? undefined;
                    } else if (output.schema?.case === "float") {
                        schemaType = "float";
                        floatMin = output.schema.value.min ?? undefined;
                        floatMax = output.schema.value.max ?? undefined;
                    } else if (output.schema?.case === "boolean") {
                        schemaType = "boolean";
                    }

                    let extractionType: ReportOutputExtractionType = "prompt";
                    let extractionValue = "";
                    if (output.extraction?.case === "prompt") {
                        extractionType = "prompt";
                        extractionValue = output.extraction.value || "";
                    } else if (output.extraction?.case === "command") {
                        extractionType = "command";
                        extractionValue = output.extraction.value || "";
                    }

                    return {
                        id: outputId,
                        title: output.title || "",
                        key: output.key || "",
                        schemaType,
                        stringPattern,
                        integerMin,
                        integerMax,
                        floatMin,
                        floatMax,
                        acceptanceCriteria: output.acceptanceCriteria || undefined,
                        extractionType,
                        extractionValue,
                    };
                }),
            };
        }
        // Fallback for unknown step types
        return {
            id,
            type: "command" as const,
            content: "",
        };
    });
};

export const convertWorkflowActionToFormLimits = (
    action?: PlainWorkflowAction,
): WorkflowEditFormLimitsData | undefined => {
    if (!action?.limits) {
        return undefined;
    }

    return {
        maxParallel: action.limits.maxParallel > 0 ? action.limits.maxParallel : undefined,
        maxTotal: action.limits.maxTotal > 0 ? action.limits.maxTotal : undefined,
    };
};

export const convertFormStepsToWorkflowStep = (steps: WorkflowEditFormStepData[]): PlainWorkflowStep[] => {
    const workflowSteps: PlainMessage<WorkflowStep>[] = steps.map((step): PlainMessage<WorkflowStep> => {
        switch (step.type) {
            case "command":
                return {
                    step: {
                        case: "task" as const,
                        value: {
                            command: step.content,
                        },
                    },
                };
            case "prompt":
                return {
                    step: {
                        case: "agent" as const,
                        value: {
                            prompt: step.content,
                        },
                    },
                };
            case "pr":
                return {
                    step: {
                        case: "pullRequest" as const,
                        value: {
                            title: step.title,
                            description: step.description,
                            branch: step.branch,
                            draft: step.isDraft,
                        },
                    },
                };
            case "report":
                return {
                    step: {
                        case: "report" as const,
                        value: {
                            outputs: step.outputs.map((output) => {
                                type OutputSpecSchema =
                                    | { case: "string"; value: { pattern: string } }
                                    | { case: "integer"; value: { min?: number; max?: number } }
                                    | { case: "float"; value: { min?: number; max?: number } }
                                    | { case: "boolean"; value: Record<string, never> };

                                let schema: OutputSpecSchema;
                                switch (output.schemaType) {
                                    case "string":
                                        schema = {
                                            case: "string",
                                            value: { pattern: output.stringPattern || "" },
                                        };
                                        break;
                                    case "integer":
                                        schema = {
                                            case: "integer",
                                            value: {
                                                min: output.integerMin,
                                                max: output.integerMax,
                                            },
                                        };
                                        break;
                                    case "float":
                                        schema = {
                                            case: "float",
                                            value: {
                                                min: output.floatMin,
                                                max: output.floatMax,
                                            },
                                        };
                                        break;
                                    case "boolean":
                                        schema = {
                                            case: "boolean",
                                            value: {},
                                        };
                                        break;
                                }

                                type OutputSpecExtraction =
                                    | { case: "prompt"; value: string }
                                    | { case: "command"; value: string };

                                const extraction: OutputSpecExtraction =
                                    output.extractionType === "prompt"
                                        ? { case: "prompt", value: output.extractionValue }
                                        : { case: "command", value: output.extractionValue };

                                return {
                                    title: output.title,
                                    key: output.key,
                                    schema,
                                    acceptanceCriteria: output.acceptanceCriteria,
                                    extraction,
                                };
                            }),
                        },
                    },
                };
        }
    });
    return workflowSteps;
};

export const convertTriggersToFormData = (triggers?: PlainWorkflowTrigger[]): WorkflowEditFormTriggerData[] => {
    if (!triggers) return [];

    return triggers
        .map((trigger): WorkflowEditFormTriggerData | null => {
            const id = `trigger-${uuidv4()}`;

            if (!trigger.trigger?.case) {
                console.warn("Trigger configuration ignored as the trigger type wasn't set.");
                return null;
            }

            const triggerCtx = trigger.context?.context;

            const cfg: WorkflowEditFormTriggerData = {
                id,
                config: {},
                context: {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                },
                type: trigger.trigger?.case,
            };

            switch (trigger.trigger?.case) {
                case "manual":
                    break;
                case "time":
                    if (trigger.trigger.value?.cronExpression !== undefined) {
                        cfg.config.time = {
                            cronExpression: trigger.trigger.value.cronExpression,
                        };
                    }
                    break;
                case "pullRequest":
                    if (trigger.trigger.value?.events !== undefined) {
                        cfg.config.pullRequest = {
                            events: trigger.trigger.value.events,
                            webhookId: trigger.trigger.value.webhookId,
                        };
                    }
                    break;
                default:
                    console.warn("Trigger configuration ignored, unsupported trigger type.", trigger);
                    return null;
            }

            switch (triggerCtx?.case) {
                case "projects":
                    cfg.context = {
                        type: WorkflowEditFormTriggerContextType.Projects,
                        projectIds: triggerCtx.value.projectIds,
                    };
                    break;
                case "repositories": {
                    const { repositorySelector, environmentClassId } = triggerCtx.value;
                    if (repositorySelector.case === "repoSelector") {
                        cfg.context = {
                            type: WorkflowEditFormTriggerContextType.RepositoriesSearch,
                            repoSearchString: repositorySelector.value.repoSearchString,
                            scmHost: repositorySelector.value.scmHost,
                            runnerId: "",
                            environmentClassId,
                        };
                    } else if (repositorySelector.case === "repositoryUrls") {
                        cfg.context = {
                            type: WorkflowEditFormTriggerContextType.RepositoriesList,
                            environmentClassId,
                            repoUrls: repositorySelector.value.repoUrls,
                        } satisfies WorkflowEditFormTriggerRepositoryListContext;
                    }
                    break;
                }
                default:
                    console.warn(
                        "Trigger context ignored, we only support projects or repositories context for now.",
                        trigger,
                    );
                    return null;
            }

            return cfg;
        })
        .filter((t): t is WorkflowEditFormTriggerData => t !== null);
};

export const convertFormTriggersToProtobuf = (triggers: WorkflowEditFormTriggerData[]): PlainWorkflowTrigger[] => {
    return triggers.map((trigger) => {
        const triggerCtx = convertFormContextToProtobuf(trigger.context);
        if (trigger.config.pullRequest) {
            return new WorkflowTrigger({
                trigger: {
                    case: "pullRequest",
                    value: new WorkflowTrigger_PullRequest({
                        events: trigger.config.pullRequest.events,
                        webhookId: trigger.config.pullRequest.webhookId,
                    }),
                },
                context: triggerCtx,
            });
        }

        if (trigger.config.time) {
            return new WorkflowTrigger({
                trigger: {
                    case: "time",
                    value: new WorkflowTrigger_Time({
                        cronExpression: trigger.config.time.cronExpression,
                    }),
                },
                context: triggerCtx,
            });
        }

        return new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: triggerCtx,
        });
    });
};

export const convertFormContextToProtobuf = (
    context: WorkflowEditFormTriggerContext,
): WorkflowTriggerContext | undefined => {
    let triggerCtx: WorkflowTriggerContext | undefined;
    switch (context.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            triggerCtx = new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: context.projectIds,
                    }),
                },
            });
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
            triggerCtx = new WorkflowTriggerContext({
                context: {
                    case: "repositories",
                    value: new WorkflowTriggerContext_Repositories({
                        repositorySelector: {
                            case: "repoSelector",
                            value: {
                                repoSearchString: context.repoSearchString,
                                scmHost: context.scmHost,
                            },
                        },
                        environmentClassId: context.environmentClassId,
                    }),
                },
            });
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            triggerCtx = new WorkflowTriggerContext({
                context: {
                    case: "repositories",
                    value: new WorkflowTriggerContext_Repositories({
                        repositorySelector: {
                            case: "repositoryUrls",
                            value: {
                                repoUrls: context.repoUrls,
                            },
                        },
                        environmentClassId: context.environmentClassId,
                    }),
                },
            });
            break;
        default:
            break;
    }
    return triggerCtx;
};

export const mergeFormDataIntoWorkflow = (workflow: PlainWorkflow, formData: WorkflowEditFormData): PlainWorkflow => {
    const triggers = convertFormTriggersToProtobuf(formData.triggers);
    const steps = convertFormStepsToWorkflowStep(formData.steps);

    return {
        ...workflow,
        spec: {
            deleting: workflow.spec?.deleting ?? false,
            triggers,
            action: {
                ...workflow.spec?.action,
                steps,
                limits: formData.limits
                    ? {
                          maxParallel: formData.limits.maxParallel ?? 0,
                          maxTotal: formData.limits.maxTotal ?? 0,
                      }
                    : workflow.spec?.action?.limits,
            },
        },
    };
};

/**
 * Extracts the trigger context from a workflow's first trigger with a context and converts it to form format.
 * Checks all trigger types (manual, pullRequest, time) for context configuration.
 */
export const getInitialContext = (workflow: PlainWorkflow): WorkflowEditFormTriggerContext => {
    const triggers = workflow?.spec?.triggers ?? [];

    for (const trigger of triggers) {
        const triggerCtx = trigger.context?.context;
        if (!triggerCtx) {
            continue;
        }

        switch (triggerCtx.case) {
            case "projects":
                return {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: triggerCtx.value.projectIds ?? [],
                };
            case "repositories": {
                const { repositorySelector, environmentClassId } = triggerCtx.value;
                if (repositorySelector?.case === "repoSelector") {
                    return {
                        type: WorkflowEditFormTriggerContextType.RepositoriesSearch,
                        repoSearchString: repositorySelector.value.repoSearchString ?? "",
                        scmHost: repositorySelector.value.scmHost ?? "",
                        runnerId: "",
                        environmentClassId: environmentClassId ?? "",
                    };
                } else if (repositorySelector?.case === "repositoryUrls") {
                    return {
                        type: WorkflowEditFormTriggerContextType.RepositoriesList,
                        environmentClassId: environmentClassId ?? "",
                        repoUrls: repositorySelector.value.repoUrls ?? [],
                    };
                }
                break;
            }
        }
    }

    return {
        type: WorkflowEditFormTriggerContextType.Projects,
        projectIds: [],
    };
};

/**
 * Compares two step arrays for equality (ignoring temporary IDs).
 */
export const areStepsEqual = (
    a: WorkflowEditFormStepData[] | null,
    b: WorkflowEditFormStepData[] | null,
): boolean => {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    if (a.length !== b.length) return false;

    return a.every((stepA, index) => {
        const stepB = b[index];
        if (stepA.type !== stepB.type) return false;

        switch (stepA.type) {
            case "command":
            case "prompt":
                return stepA.content === (stepB as typeof stepA).content;
            case "pr":
                return (
                    stepA.title === (stepB as typeof stepA).title &&
                    stepA.description === (stepB as typeof stepA).description &&
                    stepA.branch === (stepB as typeof stepA).branch &&
                    stepA.isDraft === (stepB as typeof stepA).isDraft
                );
            case "report": {
                const outputsB = (stepB as typeof stepA).outputs;
                if (stepA.outputs.length !== outputsB.length) return false;
                return stepA.outputs.every((outputA, outputIndex) => {
                    const outputB = outputsB[outputIndex];
                    return (
                        outputA.title === outputB.title &&
                        outputA.key === outputB.key &&
                        outputA.schemaType === outputB.schemaType &&
                        outputA.stringPattern === outputB.stringPattern &&
                        outputA.integerMin === outputB.integerMin &&
                        outputA.integerMax === outputB.integerMax &&
                        outputA.floatMin === outputB.floatMin &&
                        outputA.floatMax === outputB.floatMax &&
                        outputA.acceptanceCriteria === outputB.acceptanceCriteria &&
                        outputA.extractionType === outputB.extractionType &&
                        outputA.extractionValue === outputB.extractionValue
                    );
                });
            }
        }
    });
};

/**
 * Compares two trigger context objects for equality.
 */
const areTriggerContextsEqual = (a: WorkflowEditFormTriggerContext, b: WorkflowEditFormTriggerContext): boolean => {
    if (a.type !== b.type) return false;

    switch (a.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            return arraysEqual(a.projectIds, (b as typeof a).projectIds);
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
            return (
                a.repoSearchString === (b as typeof a).repoSearchString &&
                a.scmHost === (b as typeof a).scmHost &&
                a.runnerId === (b as typeof a).runnerId &&
                a.environmentClassId === (b as typeof a).environmentClassId
            );
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            return (
                a.environmentClassId === (b as typeof a).environmentClassId &&
                arraysEqual(a.repoUrls, (b as typeof a).repoUrls)
            );
    }
};

/**
 * Compares two trigger config objects for equality.
 */
const areTriggerConfigsEqual = (
    a: WorkflowEditFormTriggerConfigurationData,
    b: WorkflowEditFormTriggerConfigurationData,
): boolean => {
    // Compare time config
    if (a.time?.cronExpression !== b.time?.cronExpression) return false;

    // Compare pullRequest config
    const eventsA = a.pullRequest?.events ?? [];
    const eventsB = b.pullRequest?.events ?? [];
    if (!arraysEqual(eventsA, eventsB)) return false;

    return true;
};

/**
 * Compares two trigger arrays for equality (ignoring temporary IDs).
 */
export const areTriggersEqual = (a: WorkflowEditFormTriggerData[], b: WorkflowEditFormTriggerData[]): boolean => {
    if (a.length !== b.length) return false;

    return a.every((triggerA, index) => {
        const triggerB = b[index];
        return (
            triggerA.type === triggerB.type &&
            areTriggerContextsEqual(triggerA.context, triggerB.context) &&
            areTriggerConfigsEqual(triggerA.config, triggerB.config)
        );
    });
};

/**
 * Compares two executor subjects for equality.
 */
export const areExecutorsEqual = (
    a: PlainMessage<Subject> | undefined,
    b: PlainMessage<Subject> | undefined,
): boolean => {
    if (a === undefined && b === undefined) return true;
    if (a === undefined || b === undefined) return false;
    return a.id === b.id;
};

/**
 * Compares two limits for equality.
 */
export const areLimitsEqual = (
    a: WorkflowEditFormLimitsData | undefined,
    b: WorkflowEditFormLimitsData | undefined,
): boolean => {
    return a?.maxParallel === b?.maxParallel && a?.maxTotal === b?.maxTotal;
};

/**
 * Helper to compare two arrays for equality.
 */
const arraysEqual = <T>(a: T[], b: T[]): boolean => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
};

/**
 * Converts a workflow to form data for editing.
 */
export const convertWorkflowToFormData = (workflow: PlainWorkflow): WorkflowEditFormData => {
    const steps = workflow.spec?.action ? convertWorkflowActionToFormSteps(workflow.spec.action) : [];
    const triggers = workflow.spec?.triggers?.[0] ? convertTriggersToFormData(workflow.spec.triggers) : [];
    const executor = workflow.metadata?.executor;
    const limits = convertWorkflowActionToFormLimits(workflow.spec?.action);

    return {
        steps,
        triggers,
        executor,
        limits,
    };
};
